import type {
  WorkspacePresetsFieldsFragment,
  GetWorkspacesPreferencesQuery,
  GetWorkspacesPreferencesQueryVariables,
  WindowType,
  WindowState,
  ScreenName,
} from 'generated/graphql';
import {
  memo,
  useEffect,
  useState,
  useCallback,
  useRef,
  Dispatch,
  SetStateAction,
  useMemo,
} from 'react';
import { Colors } from 'styles';
import Dialog from '@mui/material/Dialog';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import Switch from '@mui/material/Switch';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DoneIcon from '@mui/icons-material/Done';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { css, keyframes } from 'styled-components';
import type { Keyframes } from 'styled-components';
import { useQuery } from '@apollo/client';
import equals from 'ramda/es/equals';
import splitEvery from 'ramda/es/splitEvery';
import { BroadcastChannel } from 'broadcast-channel';
import useBroadcastChannel from 'hooks/useBroadcastChannel';
import { GET_WORKSPACES_PREFERENCES } from 'modules/Apollo/queries';
import { useWorkspaceMutations } from './useWorkspaceMutations';
import { useWorkspaceActions, getMostRecentlyUpdatedPreset } from './useWorkspaceActions';
import Input from 'common/ui/Input';
import { AppRoutePath } from 'config/constants';
import { useCurrentCaseId } from 'hooks/useCurrentCase';
import { getScreenName } from 'utils/screenNames';
import {
  addExtensionListener,
  applyWorkspacePresetEvent,
  initEvent,
  refreshEvent,
  removeExtensionListener,
  dispatchExtensionEvent,
} from 'domains/extension/extensionEventCreators';
import { RESPONSE_EVENTS as EXTENSION_RESPONSE_EVENTS } from 'domains/extension/constants';
import {
  CrossWindowDataRefetchType,
  NameSpaces,
  useCrossTabEventsListener,
} from 'modules/EventsManager';
import { telemetryContextFor } from 'modules/Apollo/types';

const flashAnimation = ({
  rect: { top = 0, left = 0, bottom = window.innerHeight, right = window.innerWidth },
}: {
  rect: DOMRect;
}): Keyframes => keyframes`
0% {
  border-radius: 0;
}
95% {
  opacity: 1;
  border-radius: 10px;
  top: ${Math.round(top)}px;
  left: ${Math.round(left)}px;
  bottom: ${Math.round(window.innerHeight - bottom)}px;
  right: ${Math.round(window.innerWidth - right)}px;
}

100% {
  opacity: 0;
  border-radius: 10px;
  top: ${Math.round(top)}px;
  left: ${Math.round(left)}px;
  bottom: ${Math.round(window.innerHeight - bottom)}px;
  right: ${Math.round(window.innerWidth - right)}px;
}
`;

const CameraFlash = ({ rect }: { rect: DOMRect }) => (
  <div
    css={css`
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      background-color: white;
      z-index: 1;
      animation-name: ${flashAnimation({ rect })};
      animation-duration: 0.5s;
      animation-timing-function: linear;
      animation-fill-mode: forwards;
      pointer-events: none;
    `}
  />
);

const KEY = 'workspace-manager' as const;

type WorkspaceManagerBroadcastChannelMessage = {
  type: 'close' | 'open' | 'flash';
};

export const useWorkspaceManagerChannel =
  (): BroadcastChannel<WorkspaceManagerBroadcastChannelMessage> =>
    useBroadcastChannel<WorkspaceManagerBroadcastChannelMessage>(KEY);

const useFlash = ({
  refetch,
}: {
  refetch: () => Promise<unknown>;
}): [boolean, () => void, Dispatch<SetStateAction<boolean>>] => {
  const [capturing, setCapturing] = useState<boolean>(false);
  const didUnmount = useRef(false);

  useEffect(
    () => () => {
      didUnmount.current = true;
    },
    []
  );

  const flash = useCallback(() => {
    setCapturing(true);
    setTimeout(() => {
      if (didUnmount.current === false) {
        refetch();
        setCapturing(false);
      }
    });
  }, [refetch]);

  return [capturing, flash, setCapturing];
};

export type BrowserWindow = {
  id: string;
  width: number;
  height: number;
  top: number;
  left: number;
  type: WindowType;
  state: WindowState;
  tabs: Array<{
    url: string;
  }>;
  screen: ScreenName;
};

type Detail = {
  instanceId: string;
  windows: Array<Omit<BrowserWindow, 'screen'>>;
};

type SystemInfo = {
  instanceId: string;
  windows: Array<Omit<BrowserWindow, 'tabs'>>;
};

export const mapSystemInfo = (detail: Detail): SystemInfo => ({
  instanceId: detail.instanceId,
  windows: Array.from(
    new Map(
      detail.windows
        .filter((window) => !window.tabs[0]?.url.includes(AppRoutePath.PIXEL_WORKER))
        .map((window) => ({
          id: window.id,
          width: window.width,
          height: window.height,
          top: window.top,
          left: window.left,
          type: window.type,
          state: window.state,
          screen: getScreenName(window.tabs[0].url),
        }))
        .map((item) => [item.screen, item])
    ).values()
  ),
});

const getSystemInfo = (): Promise<SystemInfo> =>
  new Promise((resolve) => {
    const handler = (event: Event) => {
      const { detail } = event as CustomEvent<Detail>;
      removeExtensionListener(EXTENSION_RESPONSE_EVENTS.SYSTEM_INFO_UPDATED, handler);

      const systemInfo = mapSystemInfo({
        ...detail,
        windows: detail.windows.filter(
          (w) => w.tabs.length > 0 && w.tabs[0].url.includes(window.location.hostname)
        ),
      });

      resolve({
        ...systemInfo,
        windows: Array.from(
          new Map(systemInfo.windows.map((item) => [item.screen, item])).values()
        ),
      });
    };

    dispatchExtensionEvent(refreshEvent());
    addExtensionListener(EXTENSION_RESPONSE_EVENTS.SYSTEM_INFO_UPDATED, handler);
  });

export const computeSingleDisplayGrid = (
  windows: Array<Omit<BrowserWindow, 'tabs'>>
): Array<Omit<BrowserWindow, 'tabs'>> => {
  const root = Math.ceil(Math.sqrt(windows.length));

  const width = Math.floor(window.screen.availWidth / root);
  const height = Math.floor(
    window.screen.availHeight / Math.ceil(Math.min(root, windows.length / root))
  );

  return splitEvery(root, windows)
    .map((chunk, row) =>
      chunk.map((win, col) => ({
        ...win,
        height,
        width,
        top: row * height,
        left: col * width,
      }))
    )
    .flat(1);
};

const WorkspaceManager = memo(() => {
  const [showModal, setShowModal] = useState(false);
  const [, setSystemInfo] = useState<SystemInfo | null>(null);
  const [renaming, setRenaming] = useState<Nullable<string>>(null);
  const captureButtonRef = useRef<HTMLLIElement>(null);
  const [rect, setRect] = useState<Nullable<DOMRect>>(null);

  const {
    data: toolPreferencesResults,
    refetch,
    loading,
  } = useQuery<GetWorkspacesPreferencesQuery, GetWorkspacesPreferencesQueryVariables>(
    GET_WORKSPACES_PREFERENCES,
    { context: APOLLO_QUERY_CONTEXT }
  );

  const toolPreferences = toolPreferencesResults?.toolPreferences;

  const workspacePresets = useMemo(
    () => toolPreferences?.workspaces.presets || [],
    [toolPreferences?.workspaces.presets]
  );

  const mostRecentPreset = getMostRecentlyUpdatedPreset(workspacePresets);

  const autoApply = toolPreferences?.workspaces.autoApply ?? false;
  const [capturing, flash, setCapturing] = useFlash({ refetch });
  const [editingSmid, setEditingSmid] = useState<Nullable<string>>(null);

  const channel = useWorkspaceManagerChannel();

  useCrossTabEventsListener(NameSpaces.CROSS_WINDOW_DATA_REFETCH, ({ source, payload }) => {
    if (source === 'remote' && payload.type === CrossWindowDataRefetchType.TOOL_PREFERENCES) {
      refetch();
    }
  });

  useEffect(() => {
    const handler = (msg: WorkspaceManagerBroadcastChannelMessage) => {
      switch (msg.type) {
        case 'flash':
          flash();
          break;
        case 'open':
          setShowModal(true);
          break;
        case 'close':
          setCapturing(false);
          setShowModal(false);
          break;
        default:
      }
    };

    channel.addEventListener('message', handler);
    return () => channel.removeEventListener('message', handler);
  }, [channel, flash, setCapturing]);

  const {
    createWorkspacePreset,
    updateWorkspacePreset,
    setWorkspaceAutoApply,
    deleteWorkspacePreset,
  } = useWorkspaceMutations({ context: APOLLO_QUERY_CONTEXT });

  const { handlePresetSelection } = useWorkspaceActions({ context: APOLLO_QUERY_CONTEXT });

  const handleUpsertWorkspacePreset = useCallback(async () => {
    const systemInfo = await getSystemInfo();
    setEditingSmid(null);
    setRect(captureButtonRef.current?.getBoundingClientRect());
    channel.postMessage({ type: 'flash' });
    flash();

    const toUpdate = workspacePresets.find((x) => x.smid === editingSmid);

    const payload = {
      instanceId: systemInfo.instanceId,
      name: toUpdate?.name || `${systemInfo.windows.length} windows`,
      windows: systemInfo.windows,
    } as const;

    let smid: string | undefined;

    if (editingSmid == null) {
      const response = await createWorkspacePreset({ variables: { payload } });
      smid = response?.data?.createWorkspacePreset?.smid;
    } else {
      const response = await updateWorkspacePreset({
        variables: { smid: editingSmid, payload },
      });
      smid = response?.data?.updateWorkspacePreset?.smid;
    }

    setRenaming(smid);
  }, [channel, flash, workspacePresets, editingSmid, createWorkspacePreset, updateWorkspacePreset]);

  const handleShowModal = useCallback(() => {
    if (showModal) setCapturing(false);
    channel.postMessage({ type: showModal ? 'close' : 'open' });
    setShowModal((show) => !show);
  }, [channel, setCapturing, showModal]);

  useEffect(() => {
    if (!showModal) return;

    getSystemInfo().then((systemInfo) => {
      setSystemInfo((prevSystemInfo) =>
        !equals(systemInfo, prevSystemInfo) ? systemInfo : prevSystemInfo
      );
    });
  }, [showModal]);

  useEffect(() => {
    dispatchExtensionEvent(initEvent());
  }, []);

  useEffect(() => {
    addExtensionListener(EXTENSION_RESPONSE_EVENTS.ICON_CLICKED, handleShowModal);
    return () => removeExtensionListener(EXTENSION_RESPONSE_EVENTS.ICON_CLICKED, handleShowModal);
  }, [handleShowModal]);

  const handleRename = useCallback(
    async (evt: React.SyntheticEvent<HTMLFormElement>) => {
      evt.preventDefault();
      if (!(evt.target instanceof HTMLFormElement)) return;

      const name = new FormData(evt.target).get('name')?.toString();
      if (renaming == null || name == null) return;

      const toUpdate = workspacePresets.find(
        (workspacePreset) => workspacePreset.smid === renaming
      );
      if (!toUpdate) return;

      const { instanceId, windows } = toUpdate;

      await updateWorkspacePreset({
        variables: {
          smid: renaming,
          payload: {
            instanceId,
            windows: windows.map(({ __typename, ...win }) => win),
            name,
          },
        },
      });

      setRenaming(null);
    },
    [renaming, updateWorkspacePreset, workspacePresets]
  );

  const currentCaseId = useCurrentCaseId();

  const applyWorkspacePreset = useCallback(
    (
      workspacePreset:
        | { instanceId: string; windows: Omit<BrowserWindow, 'tabs'>[] }
        | ({ readonly __typename?: 'WorkspacePreset' } & WorkspacePresetsFieldsFragment)
    ) => {
      if (currentCaseId == null) return null;
      dispatchExtensionEvent(
        applyWorkspacePresetEvent({ preset: workspacePreset, caseId: currentCaseId })
      );
    },
    [currentCaseId]
  );

  const toggleAutoApply = useCallback(() => {
    if (toolPreferences == null) return;

    setWorkspaceAutoApply({
      variables: { value: !autoApply },
      optimisticResponse: {
        __typename: 'Mutation',
        setWorkspaceAutoApply: {
          __typename: 'WorkspacePreferences',
          autoApply: !autoApply,
        },
      },
      update: (proxy) => {
        proxy.writeQuery({
          query: GET_WORKSPACES_PREFERENCES,
          data: {
            toolPreferences: {
              ...toolPreferences,
              workspaces: {
                ...toolPreferences.workspaces,
                autoApply: !autoApply,
              },
            },
          },
        });
      },
    });
  }, [autoApply, toolPreferences, setWorkspaceAutoApply]);

  const [dropdownButton, setDropdownButton] = useState<HTMLElement | null>(null);

  const handleOpenDropdown = ({ target, smid }: { target: HTMLElement; smid: string }) => {
    setDropdownButton(target);
    setEditingSmid(smid);
  };

  const handleCloseDropdown = useCallback(() => {
    setDropdownButton(null);
    setEditingSmid(null);
  }, []);

  const handleDeletePreset = useCallback(() => {
    if (editingSmid == null) return;

    deleteWorkspacePreset({
      variables: { smid: editingSmid },
      optimisticResponse: {
        __typename: 'Mutation',
        deleteWorkspacePreset: {
          __typename: 'ConfirmDeleteWorkspacePreset',
          smid: editingSmid,
        },
      },
      update: (proxy) => {
        proxy.writeQuery({
          query: GET_WORKSPACES_PREFERENCES,
          data: {
            toolPreferences: {
              ...toolPreferences,
              workspaces: {
                ...toolPreferences?.workspaces,
                presets: toolPreferences?.workspaces.presets.filter(
                  (x: any) => x.smid !== editingSmid
                ),
              },
            },
          },
        });
      },
    });

    setEditingSmid(null);
  }, [deleteWorkspacePreset, editingSmid, toolPreferences]);

  return (
    <>
      {!showModal && capturing && rect && <CameraFlash rect={rect} />}

      <Dialog
        open={!loading && showModal}
        onClose={handleShowModal}
        slotProps={{
          container: {
            sx: {
              alignItems: 'flex-end',
              justifyContent: 'flex-start',
              pl: '45px',
            },
          },
          paper: {
            sx: {
              minWidth: 240,
            },
          },
        }}
      >
        {capturing && rect && <CameraFlash rect={rect} />}

        <Typography
          variant="h6"
          sx={{
            padding: '16px 16px 0px 16px',
            fontSize: '13px',
            lineHeight: '17px',
            fontWeight: 400,
            color: Colors.gray8,
          }}
        >
          Workspace Layout
        </Typography>

        {workspacePresets.length > 0 && (
          <>
            <List>
              <ListItem
                secondaryAction={
                  <Switch edge="end" onClick={toggleAutoApply} checked={autoApply} size="small" />
                }
              >
                <ListItemText
                  primary="Auto-apply selected layout"
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontSize: '13px',
                      lineHeight: '17px',
                      fontWeight: 400,
                    },
                  }}
                />
              </ListItem>
            </List>

            <Divider />

            <List>
              {workspacePresets.map((workspacePreset) => {
                const isRenaming = renaming === workspacePreset.smid;
                const isMostRecent = workspacePreset.smid === mostRecentPreset?.smid;

                const checkVisible = Boolean(isMostRecent && autoApply);

                return (
                  <Box
                    component="form"
                    onSubmit={handleRename}
                    key={workspacePreset.smid}
                    sx={{ display: 'contents' }}
                  >
                    <ListItem
                      onClick={() =>
                        !isRenaming &&
                        handlePresetSelection(
                          workspacePreset,
                          workspacePresets,
                          applyWorkspacePreset
                        )
                      }
                      sx={{
                        cursor: isRenaming ? 'default' : 'pointer',
                        '&:hover .srna-checkIcon': { opacity: 1 },
                      }}
                      secondaryAction={
                        <IconButton
                          type={isRenaming ? 'submit' : 'button'}
                          edge="end"
                          aria-label="settings"
                          onClick={(evt) => {
                            if (isRenaming) return;
                            handleOpenDropdown({
                              target: evt.currentTarget,
                              smid: workspacePreset.smid,
                            });
                          }}
                          sx={{
                            p: '6px',
                            color: Colors.gray8,
                            '&:hover': { color: Colors.white },
                          }}
                          size="large"
                        >
                          {isRenaming ? <DoneIcon /> : <MoreVertIcon />}
                        </IconButton>
                      }
                    >
                      {isRenaming ? (
                        <Input name="name" defaultValue={workspacePreset.name} />
                      ) : (
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <DoneIcon
                                sx={{
                                  fontSize: 'small',
                                  mr: '8px',
                                  opacity: checkVisible ? 1 : 0,
                                  transition: 'opacity 0.2s',
                                }}
                              />
                              {workspacePreset.name}
                            </Box>
                          }
                          sx={{
                            '& .MuiListItemText-primary': {
                              fontSize: '13px',
                              lineHeight: '17px',
                              fontWeight: 400,
                            },
                          }}
                        />
                      )}
                    </ListItem>
                  </Box>
                );
              })}
            </List>
          </>
        )}

        <List sx={{ paddingTop: 0 }}>
          <ListItem
            onClick={handleUpsertWorkspacePreset}
            ref={captureButtonRef}
            sx={{ cursor: 'pointer' }}
          >
            <ListItemText
              primary="+ Capture new workspace layout"
              sx={{
                '& .MuiListItemText-primary': {
                  fontSize: '13px',
                  lineHeight: '17px',
                  fontWeight: 400,
                  color: Colors.blue2,
                },
              }}
            />
          </ListItem>
        </List>

        <Menu
          anchorEl={dropdownButton}
          keepMounted
          open={editingSmid != null}
          onClose={handleCloseDropdown}
          slotProps={{
            paper: { sx: { minWidth: 240 } },
          }}
        >
          <MenuItem
            onClick={handleUpsertWorkspacePreset}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Typography sx={{ fontSize: '13px', lineHeight: '17px', fontWeight: 400 }}>
              Update
            </Typography>
            <RefreshIcon />
          </MenuItem>

          <MenuItem
            onClick={handleDeletePreset}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Typography sx={{ fontSize: '13px', lineHeight: '17px', fontWeight: 400 }}>
              Delete
            </Typography>
            <DeleteForeverIcon />
          </MenuItem>
        </Menu>
      </Dialog>
    </>
  );
});

WorkspaceManager.displayName = 'WorkspaceManager';

export const toggleModal = (): unknown =>
  document.dispatchEvent(new CustomEvent('srna.icon.clicked'));

export default WorkspaceManager;

const APOLLO_QUERY_CONTEXT = telemetryContextFor(WorkspaceManager);
